import React, { useCallback, useEffect, useRef, useState } from "react";
import { Brush, Eraser, Trash2 } from "lucide-react";

interface MaskCanvasProps {
  imageUrl: string;
  // Emits a base64 PNG mask (white = edit, black = keep), or null when empty.
  onMaskChange: (maskB64: string | null) => void;
}

const TINT = "rgba(139, 92, 246, 0.5)"; // violet brand, semi-transparent

/**
 * Paint a mask over a reference image.
 *
 * State of record is an offscreen "strokes" canvas at the image's *natural*
 * resolution: transparent background, opaque-white strokes (erase = clear back
 * to transparent). From it we derive:
 *   - the visible overlay (strokes re-tinted violet), repainted on every change
 *     and on resize — so rotating/resizing never loses the visible mask;
 *   - the export PNG (white strokes composited onto a black background).
 *
 * The reference image is rendered as a plain <img> underneath and is never
 * drawn onto any exported canvas, so a cross-origin (presigned S3) reference
 * cannot taint the export.
 */
const MaskCanvas: React.FC<MaskCanvasProps> = ({ imageUrl, onMaskChange }) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const overlayRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<HTMLCanvasElement | null>(null); // offscreen, natural res

  const painting = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const hasPaint = useRef(false);
  const natural = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

  const [brushSize, setBrushSize] = useState(40);
  const [mode, setMode] = useState<"paint" | "erase">("paint");
  const [ready, setReady] = useState(false);

  // Repaint the visible overlay from the strokes canvas (violet-tinted).
  const repaintOverlay = useCallback(() => {
    const overlay = overlayRef.current;
    const strokes = strokesRef.current;
    if (!overlay) return;
    const dispW = overlay.clientWidth;
    const dispH = overlay.clientHeight;
    const octx = overlay.getContext("2d");
    if (!octx) return;
    octx.save();
    octx.setTransform(1, 0, 0, 1, 0, 0);
    octx.clearRect(0, 0, overlay.width, overlay.height);
    octx.restore();
    if (!strokes) return;
    const dpr = window.devicePixelRatio || 1;
    octx.save();
    octx.setTransform(dpr, 0, 0, dpr, 0, 0);
    octx.globalCompositeOperation = "source-over";
    octx.drawImage(strokes, 0, 0, dispW, dispH);
    octx.globalCompositeOperation = "source-in";
    octx.fillStyle = TINT;
    octx.fillRect(0, 0, dispW, dispH);
    octx.restore();
  }, []);

  const syncSizes = useCallback(() => {
    const img = imgRef.current;
    const overlay = overlayRef.current;
    if (!img || !overlay || !img.complete || !img.naturalWidth) return;

    const dpr = window.devicePixelRatio || 1;
    const dispW = img.clientWidth;
    const dispH = img.clientHeight;
    const wantW = Math.max(1, Math.round(dispW * dpr));
    const wantH = Math.max(1, Math.round(dispH * dpr));
    if (overlay.width !== wantW || overlay.height !== wantH) {
      overlay.width = wantW;
      overlay.height = wantH;
    }
    overlay.style.width = `${dispW}px`;
    overlay.style.height = `${dispH}px`;

    if (!strokesRef.current) {
      const s = document.createElement("canvas");
      s.width = img.naturalWidth;
      s.height = img.naturalHeight;
      const sctx = s.getContext("2d");
      if (sctx) {
        sctx.lineCap = "round";
        sctx.lineJoin = "round";
      }
      strokesRef.current = s;
      natural.current = { w: img.naturalWidth, h: img.naturalHeight };
    }
    setReady(true);
    repaintOverlay();
  }, [repaintOverlay]);

  // Reset everything when the reference image changes.
  useEffect(() => {
    strokesRef.current = null;
    hasPaint.current = false;
    natural.current = { w: 0, h: 0 };
    setReady(false);
    onMaskChange(null);
    const img = imgRef.current;
    if (img && img.complete) syncSizes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  useEffect(() => {
    window.addEventListener("resize", syncSizes);
    return () => window.removeEventListener("resize", syncSizes);
  }, [syncSizes]);

  const pointFromEvent = (e: React.PointerEvent) => {
    const overlay = overlayRef.current!;
    const rect = overlay.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const drawSeg = (from: { x: number; y: number }, to: { x: number; y: number }) => {
    const overlay = overlayRef.current;
    const strokes = strokesRef.current;
    if (!overlay || !strokes) return;
    const sctx = strokes.getContext("2d");
    if (!sctx) return;

    const dispW = overlay.clientWidth || 1;
    const scale = natural.current.w / dispW; // display px → natural px

    sctx.globalCompositeOperation = mode === "paint" ? "source-over" : "destination-out";
    sctx.strokeStyle = "#fff";
    sctx.lineWidth = Math.max(1, brushSize * scale);
    sctx.beginPath();
    sctx.moveTo(from.x * scale, from.y * scale);
    sctx.lineTo(to.x * scale, to.y * scale);
    sctx.stroke();

    if (mode === "paint") hasPaint.current = true;
    repaintOverlay();
  };

  const emitMask = () => {
    const strokes = strokesRef.current;
    if (!strokes || !hasPaint.current) {
      onMaskChange(null);
      return;
    }
    try {
      const out = document.createElement("canvas");
      out.width = strokes.width;
      out.height = strokes.height;
      const octx = out.getContext("2d");
      if (!octx) throw new Error("no ctx");
      octx.fillStyle = "#000";
      octx.fillRect(0, 0, out.width, out.height);
      octx.drawImage(strokes, 0, 0);
      onMaskChange(out.toDataURL("image/png").split(",")[1] || null);
    } catch (err) {
      console.error("Mask export failed:", err);
      onMaskChange(null);
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!ready) return;
    e.preventDefault();
    overlayRef.current?.setPointerCapture(e.pointerId);
    painting.current = true;
    const p = pointFromEvent(e);
    lastPoint.current = p;
    drawSeg(p, p);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!painting.current) return;
    const p = pointFromEvent(e);
    drawSeg(lastPoint.current || p, p);
    lastPoint.current = p;
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (!painting.current) return;
    painting.current = false;
    lastPoint.current = null;
    overlayRef.current?.releasePointerCapture(e.pointerId);
    emitMask();
  };

  const clearMask = () => {
    const strokes = strokesRef.current;
    if (strokes) {
      const sctx = strokes.getContext("2d");
      sctx?.clearRect(0, 0, strokes.width, strokes.height);
    }
    hasPaint.current = false;
    repaintOverlay();
    onMaskChange(null);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Brush toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border-default px-3 py-2">
        <div className="flex gap-1 rounded-md bg-surface-2 p-0.5">
          <button
            type="button"
            onClick={() => setMode("paint")}
            className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${
              mode === "paint" ? "bg-surface-overlay text-primary shadow-sm" : "text-muted"
            }`}
          >
            <Brush size={13} /> Paint
          </button>
          <button
            type="button"
            onClick={() => setMode("erase")}
            className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${
              mode === "erase" ? "bg-surface-overlay text-primary shadow-sm" : "text-muted"
            }`}
          >
            <Eraser size={13} /> Erase
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-subtle">Brush</span>
          <input
            type="range"
            min={5}
            max={150}
            value={brushSize}
            onChange={(e) => setBrushSize(parseInt(e.target.value, 10))}
            className="w-24 accent-violet-500 sm:w-28"
          />
          <span className="w-7 text-xs tabular-nums text-subtle">{brushSize}</span>
        </div>
        <button
          type="button"
          onClick={clearMask}
          className="ml-auto flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2 py-1 text-xs text-muted hover:text-primary hover:bg-surface-3"
        >
          <Trash2 size={13} /> Clear
        </button>
      </div>

      {/* Paint surface */}
      <div className="flex flex-1 items-center justify-center overflow-auto p-4">
        <div className="relative inline-block">
          <img
            ref={imgRef}
            src={imageUrl}
            alt="reference"
            draggable={false}
            onLoad={syncSizes}
            // No crossOrigin: the reference is never drawn onto an exported
            // canvas, so it can't taint the result — and omitting it avoids
            // load failures when the bucket's CORS doesn't cover this origin.
            className="block max-h-[68vh] max-w-full select-none rounded-lg shadow"
          />
          <canvas
            ref={overlayRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            className="absolute inset-0 cursor-crosshair touch-none rounded-lg"
          />
        </div>
      </div>

      <p className="px-3 pb-2 text-center text-xs text-subtle">
        Paint over the areas to change. White = edit, the rest is kept.
      </p>
    </div>
  );
};

export default MaskCanvas;
